import { useState } from 'react';
import type { EntryDto, Puzzle, Rank } from '../api/types';

interface Props {
  entry: EntryDto;
  puzzle: Puzzle;
  /** True für den Eintrag des aktuellen Spielers (dezente Hervorhebung). */
  isMe?: boolean;
  /** Initial expandiert zeigen? Default false (eingeklappt). */
  defaultOpen?: boolean;
  /** Steuert, ob die Rang-Badge angezeigt wird. Ränge liegen schon vor
   *  dem vollen Reveal vor (sobald man selbst getippt hat); die Badge
   *  wird aber erst ab ≥2 Einträgen gezeigt, damit kein verfrühtes 🥇
   *  erscheint, wenn man als Einziger getippt hat. */
  showRank: boolean;
}

interface AttemptRow {
  index: number;
  emojis: string | null;
  diff: number | null;
  /** Berechnet aus `solution_year + diff`, wenn beides vorliegt. */
  guess: number | null;
}

export function EntryView({ entry, puzzle, showRank, isMe = false, defaultOpen = false }: Props) {
  const [open, setOpen] = useState(defaultOpen);
  const rows = collectAttempts(entry, puzzle);
  const displayRank = entry.rank;
  // Lösungsjahr ist bereits maskiert (siehe maskForPlayer im Backend);
  // sobald es vorliegt, darf die Jahres-Rekonstruktion gezeigt werden,
  // auch vor Reveal.
  const showSolution = puzzle.solution_year != null;

  const headLabel = entry.solved ? `${entry.attempts}/4 ✅` : 'x/4 🟥';

  return (
    <div className={`entry-row${isMe ? ' entry-row--me' : ''}${open ? ' entry-row--open' : ''}`}>
      <button
        type="button"
        className="entry-row__head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="entry-row__rank">
          {showRank && displayRank != null
            ? displayRank === 'DNF'
              ? <span className="badge badge--rank-dnf">DNF</span>
              : <span className={`badge badge--rank-${rankClass(displayRank)}`}>{rankBadge(displayRank)}</span>
            : <span className="entry-row__rank-placeholder">·</span>}
        </span>
        <span className="entry-row__name">
          {entry.player_name}
          {isMe && <span className="entry-row__me-marker"> (du)</span>}
        </span>
        <span className="entry-row__result">{headLabel}</span>
        <span className="entry-row__chevron" aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <ol className="attempts">
          {rows.map((row) => (
            <li key={row.index} className="attempts__row">
              <span className="attempts__emojis">{row.emojis ?? '—'}</span>
              <span className="attempts__diff">
                {row.diff != null ? formatDiff(row.diff) : ''}
              </span>
              {showSolution && row.guess != null && (
                <span className="attempts__guess">
                  Tipp <strong>{row.guess}</strong>
                </span>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function collectAttempts(entry: EntryDto, puzzle: Puzzle): AttemptRow[] {
  const solution = puzzle.solution_year;
  const slots: ReadonlyArray<readonly [number, string | null, number | null]> = [
    [1, entry.attempt_1_emojis, entry.attempt_1_diff],
    [2, entry.attempt_2_emojis, entry.attempt_2_diff],
    [3, entry.attempt_3_emojis, entry.attempt_3_diff],
    [4, entry.attempt_4_emojis, entry.attempt_4_diff],
  ];
  return slots
    .filter(([index]) => index <= entry.attempts)
    .map(([index, emojis, diff]) => ({
      index,
      emojis,
      diff,
      guess: solution != null && diff != null ? solution + diff : null,
    }));
}

function formatDiff(diff: number): string {
  if (diff === 0) return '(0)';
  return diff > 0 ? `(+${diff})` : `(${diff})`;
}

function rankBadge(rank: number): string {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return `#${rank}`;
}

function rankClass(rank: Rank): string {
  if (rank === 'DNF') return 'dnf';
  if (rank === 1) return 'gold';
  if (rank === 2) return 'silver';
  if (rank === 3) return 'bronze';
  return 'other';
}
